<template>
  <teleport to="body">
    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      temporary
      :width="drawerWidth"
      class="ncrm-notification-center"
      @update:model-value="onDrawerToggle"
    >
      <div class="notification-header">
        <div class="notification-header__title">
          <span class="notification-header__icon">
            <v-icon size="22">mdi-bell-outline</v-icon>
          </span>
          <div>
            <h2>{{ $t("ncrm.notifications.title") }}</h2>
            <p>{{ $t("ncrm.notifications.subtitle") }}</p>
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="$t('ncrm.notifications.close')"
          @click="drawerOpen = false"
        />
      </div>

      <v-divider />

      <div class="notification-toolbar">
        <v-chip v-if="unseenCount > 0" color="primary">
          {{ $t("ncrm.notifications.unseenCount", { count: unseenCount }) }}
        </v-chip>
        <span v-else class="notification-toolbar__status">
          {{ $t("ncrm.notifications.allSeen") }}
        </span>
        <v-btn
          v-if="items.length"
          size="small"
          variant="text"
          color="error"
          prepend-icon="mdi-delete-sweep-outline"
          :loading="dismissingAll"
          @click="dismissAll"
        >
          {{ $t("ncrm.notifications.deleteAll") }}
        </v-btn>
      </div>

      <div class="notification-content" aria-live="polite">
        <div v-if="loading" class="notification-state">
          <v-progress-circular indeterminate color="primary" size="28" />
          <span>{{ $t("ncrm.notifications.loading") }}</span>
        </div>

        <v-alert
          v-else-if="errorMessage"
          type="error"
          density="compact"
          class="ma-4"
        >
          {{ errorMessage }}
          <template #append>
            <v-btn size="small" variant="text" @click="loadItems">
              {{ $t("ncrm.notifications.retry") }}
            </v-btn>
          </template>
        </v-alert>

        <div v-else-if="!items.length" class="notification-state notification-state--empty">
          <v-icon size="38" color="secondary">mdi-bell-sleep-outline</v-icon>
          <strong>{{ $t("ncrm.notifications.emptyTitle") }}</strong>
          <span>{{ $t("ncrm.notifications.emptyDescription") }}</span>
        </div>

        <template v-else>
          <section v-if="newItems.length" class="notification-section">
            <h3>{{ $t("ncrm.notifications.sections.new") }}</h3>
            <NotificationRow
              v-for="item in newItems"
              :key="item.id"
              :item="item"
              :busy="dismissingIds.has(item.id)"
              @open="openNotification"
              @dismiss="dismissOne"
            />
          </section>

          <div v-if="remainingUnseenCount > 0" class="notification-load-more">
            <v-btn
              block
              color="primary"
              variant="tonal"
              prepend-icon="mdi-chevron-down"
              :loading="loadingMore"
              @click="loadMoreUnseen"
            >
              {{ $t("ncrm.notifications.loadMore") }}
            </v-btn>
          </div>

          <section v-if="seenItems.length" class="notification-section">
            <h3>{{ $t("ncrm.notifications.sections.seenToday") }}</h3>
            <NotificationRow
              v-for="item in seenItems"
              :key="item.id"
              :item="item"
              :busy="dismissingIds.has(item.id)"
              @open="openNotification"
              @dismiss="dismissOne"
            />
          </section>
        </template>
      </div>
    </v-navigation-drawer>
  </teleport>

  <NotificationToast
    :model-value="liveToastOpen"
    :item="liveAlert"
    @update:model-value="onLiveToastVisibility"
    @open="openLiveNotification"
  />
</template>

<script>
import { useDisplay } from "vuetify";
import NotificationRow from "./NcrmNotificationRow.vue";
import NotificationToast from "./NcrmNotificationToast.vue";
import NotificationDrawerMixin from "./ncrmNotificationDrawerMixin.mjs";
import NotificationLiveMixin from "./ncrmNotificationLiveMixin.mjs";
import { resolveNcrmNotificationRoute } from "@/Utile/ncrmNotifications.mjs";

export default {
  name: "NcrmNotificationCenter",
  components: { NotificationRow, NotificationToast },
  mixins: [NotificationDrawerMixin, NotificationLiveMixin],
  props: {
    modelValue: { type: Boolean, default: false },
    enabled: { type: Boolean, default: true },
  },
  emits: ["update:modelValue", "summary"],
  setup() {
    const { smAndDown, width: displayWidth } = useDisplay();
    return { smAndDown, displayWidth };
  },
  computed: {
    drawerOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    /**
     * Must stay numeric. Vuetify derives the closed-state `translateX` from the
     * drawer width, so a percentage string resolves to `translateX(0px)` and the
     * temporary drawer never leaves the screen after it is closed.
     */
    drawerWidth() {
      return this.smAndDown ? this.displayWidth : 420;
    },
    newItems() {
      return this.items.filter((item) => !item.seenAt);
    },
    seenItems() {
      return this.items.filter((item) => item.seenAt);
    },
    remainingUnseenCount() {
      return Math.max(0, this.unseenCount - this.newItems.length);
    },
  },
  watch: {
    enabled: {
      immediate: true,
      handler(value) {
        if (value) this.startSummaryPolling();
        else this.stopSummaryPolling();
      },
    },
    modelValue(value) {
      if (value && this.enabled) {
        this.clearLiveAlerts();
        this.loadItems();
      }
    },
  },
  methods: {
    openNotification(item) {
      const route = resolveNcrmNotificationRoute(item);
      this.drawerOpen = false;
      if (route) this.$router.push(route);
    },
    onDrawerToggle(value) {
      if (value && this.enabled && !this.loading) this.loadItems();
    },
  },
};
</script>

<style scoped src="./NcrmNotificationCenter.css"></style>
